import { useAiChatActions } from '@euler/app/flows/ai-suite/chat/AiChatActions';
import { HttpError } from '@euler/lib/request/HttpError';
import { BiAnswerAction } from '@euler/model/bi/answer';
import { BiUploadView } from '@euler/model/bi/upload';
import { useServiceFactory } from '@euler/services/factory';
import { useLocalObservable } from '@euler/utils/hooks/useLocalObservable';
import {
  catchError,
  exhaustMap,
  filter,
  from,
  map,
  retry,
  scan,
  startWith,
  switchMap,
  take,
  takeWhile,
  throwError,
  timer,
} from 'rxjs';
import { isBiUploadWorking } from './biUploadPhase';

export type BiUploadState =
  | { kind: 'loading' }
  | { kind: 'ready'; view: BiUploadView }
  /** Gone, or another member's upload. */
  | { kind: 'missing' }
  | { kind: 'failed' };

const kPollMs = 2000;
const kMaxPollBackoffMs = 15000;
/** A card stops asking after about 15 minutes; a tap starts it again. */
const kMaxPolls = 450;
/** Failed reads in a row before the card says it could not load. */
const kMaxRetries = 5;

const kUploadTaps = new Set<BiAnswerAction>([
  BiAnswerAction.SelectSheets,
  BiAnswerAction.ConfirmUpload,
  BiAnswerAction.UseExistingDataset,
]);

function isRefusal(error: unknown): boolean {
  return (
    error instanceof HttpError &&
    typeof error.status === 'number' &&
    error.status >= 400 &&
    error.status < 500
  );
}

/**
 * A file upload as the server has it. The card that takes the upload's
 * taps (`follow`) keeps asking while the system works on it, and asks
 * again after each upload tap settles; an older card reads it once.
 */
export function useBiUpload(uploadId: string, follow: boolean): BiUploadState {
  const { biService } = useServiceFactory();
  const { tapSettled$ } = useAiChatActions();
  return useLocalObservable<BiUploadState>(
    () =>
      tapSettled$.pipe(
        filter(
          tap =>
            follow && tap.kind === 'bi' && kUploadTaps.has(tap.answer.action),
        ),
        startWith(null),
        switchMap(() =>
          timer(0, kPollMs).pipe(
            take(kMaxPolls),
            exhaustMap(() => from(biService.upload(uploadId))),
            takeWhile(view => follow && isBiUploadWorking(view), true),
            // A network blip or a 5xx passes; keep asking, more slowly.
            retry({
              count: kMaxRetries,
              delay: (error: unknown, count: number) =>
                isRefusal(error)
                  ? throwError(() => error)
                  : timer(Math.min(kPollMs * 2 ** count, kMaxPollBackoffMs)),
              resetOnSuccess: true,
            }),
            map((view): BiUploadState => ({ kind: 'ready', view })),
            catchError((error: unknown) => {
              console.error('BI upload failed', uploadId, error);
              if (
                error instanceof HttpError &&
                error.code === 'resource_not_found'
              )
                return [{ kind: 'missing' } as BiUploadState];
              return [{ kind: 'failed' } as BiUploadState];
            }),
          ),
        ),
        // A failed read after the first keeps the card on screen.
        scan((current: BiUploadState, next: BiUploadState) =>
          current.kind === 'ready' && next.kind === 'failed' ? current : next,
        ),
      ),
    { kind: 'loading' },
    [biService, uploadId, follow, tapSettled$],
  );
}
